import { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import './Navbar.css';

export default function Navbar() {
  const { count } = useCart();
  const { isLoggedIn, user, logout } = useAuth();
  const [scrolled, setScrolled]     = useState(false);
  const [menuOpen, setMenuOpen]     = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [userOpen, setUserOpen]     = useState(false);
  const [query, setQuery]           = useState('');
  const navigate  = useNavigate();
  const userRef   = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Ferme le dropdown user au clic extérieur
  useEffect(() => {
    if (!userOpen) return;
    function close(e) { if (userRef.current && !userRef.current.contains(e.target)) setUserOpen(false); }
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [userOpen]);

  function handleSearch(e) {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/catalogue?q=${encodeURIComponent(query.trim())}`);
      setQuery(''); setSearchOpen(false);
    }
  }

  function handleLogout() {
    logout();
    setUserOpen(false);
    navigate('/');
  }

  const navLinks = [
    { to: '/catalogue/bijoux',    label: 'Bijoux'    },
    { to: '/catalogue/perruques', label: 'Perruques' },
    { to: '/catalogue',           label: 'Tout voir' },
  ];

  return (
    <header className={`navbar${scrolled ? ' navbar--scrolled' : ''}`}>
      <div className="navbar__inner container">

        {/* Logo */}
        <Link to="/" className="navbar__logo">
          <span className="navbar__logo-top">Les Bazars</span>
          <span className="navbar__logo-bottom">de Keassa</span>
        </Link>

        {/* Nav desktop */}
        <nav className="navbar__links" aria-label="Navigation principale">
          {navLinks.map(l => (
            <NavLink key={l.to} to={l.to}
              className={({ isActive }) => 'navbar__link' + (isActive ? ' navbar__link--active' : '')}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        {/* Actions */}
        <div className="navbar__actions">

          {/* Recherche */}
          <button className="navbar__icon-btn" aria-label="Rechercher"
            onClick={() => setSearchOpen(v => !v)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
          </button>

          {/* ── Compte utilisateur ── */}
          {isLoggedIn ? (
            <div className="navbar__user" ref={userRef}>
              <button
                className="navbar__user-btn"
                onClick={() => setUserOpen(v => !v)}
                aria-expanded={userOpen}
                aria-label={`Menu de ${user?.prenom || 'mon compte'}`}
              >
                <span className="navbar__avatar">
                  {(user?.prenom?.[0] || user?.email?.[0] || 'C').toUpperCase()}
                </span>
                <span className="navbar__user-name">{user?.prenom || 'Mon compte'}</span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                  style={{ transform: userOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }}>
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>

              {/* Dropdown */}
              {userOpen && (
                <div className="navbar__user-dropdown" role="menu">
                  <div className="navbar__user-info">
                    <p className="navbar__user-info-name">{user?.prenom} {user?.nom}</p>
                    <p className="navbar__user-info-email">{user?.email}</p>
                  </div>
                  <hr className="navbar__user-sep" />
                  {user?.role === 'admin' && (
                    <Link to="/admin" className="navbar__user-item" role="menuitem"
                      onClick={() => setUserOpen(false)} style={{ color: 'var(--or)' }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>
                      </svg>
                      Administration
                    </Link>
                  )}
                  <Link to="/mon-compte" className="navbar__user-item" role="menuitem"
                    onClick={() => setUserOpen(false)}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                    Mon compte
                  </Link>
                  <Link to="/mon-compte" className="navbar__user-item" role="menuitem"
                    onClick={() => setUserOpen(false)}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
                      <line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/>
                    </svg>
                    Mes commandes
                  </Link>
                  <hr className="navbar__user-sep" />
                  <button className="navbar__user-item navbar__user-item--danger"
                    role="menuitem" onClick={handleLogout}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                      <polyline points="16 17 21 12 16 7"/>
                      <line x1="21" y1="12" x2="9" y2="12"/>
                    </svg>
                    Se déconnecter
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/connexion" className="navbar__icon-btn" aria-label="Se connecter">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
              </svg>
            </Link>
          )}

          {/* Panier */}
          <Link to="/panier" className="navbar__icon-btn navbar__cart" aria-label={`Panier — ${count} article(s)`}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
              <line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/>
            </svg>
            {count > 0 && <span className="navbar__badge">{count}</span>}
          </Link>

          {/* Hamburger mobile */}
          <button className={`navbar__hamburger${menuOpen ? ' open' : ''}`}
            aria-label="Menu" aria-expanded={menuOpen}
            onClick={() => setMenuOpen(v => !v)}>
            <span/><span/><span/>
          </button>
        </div>
      </div>

      {/* Barre de recherche */}
      <div className={`navbar__search-bar${searchOpen ? ' navbar__search-bar--open' : ''}`}>
        <form onSubmit={handleSearch} className="container">
          <input type="search" placeholder="Rechercher un bijou, une perruque..."
            value={query} onChange={e => setQuery(e.target.value)}
            autoFocus={searchOpen} aria-label="Champ de recherche" />
          <button type="submit" className="btn-or">Rechercher</button>
        </form>
      </div>

      {/* Menu mobile */}
      <nav className={`navbar__mobile-menu${menuOpen ? ' navbar__mobile-menu--open' : ''}`}
        aria-label="Navigation mobile">
        {navLinks.map(l => (
          <NavLink key={l.to} to={l.to} className="navbar__mobile-link"
            onClick={() => setMenuOpen(false)}>{l.label}</NavLink>
        ))}
        {isLoggedIn ? (
          <>
            {user?.role === 'admin' && (
              <NavLink to="/admin" className="navbar__mobile-link" style={{ color: 'var(--or)' }}
                onClick={() => setMenuOpen(false)}>
                Administration
              </NavLink>
            )}
            <NavLink to="/mon-compte" className="navbar__mobile-link" onClick={() => setMenuOpen(false)}>
              Mon compte
            </NavLink>
            <button className="navbar__mobile-link navbar__mobile-logout"
              onClick={() => { handleLogout(); setMenuOpen(false); }}>
              Se déconnecter
            </button>
          </>
        ) : (
          <NavLink to="/connexion" className="navbar__mobile-link" onClick={() => setMenuOpen(false)}>
            Se connecter
          </NavLink>
        )}
      </nav>
    </header>
  );
}